import React, {
  Children,
  ReactNode,
  useState,
  useMemo,
  ReactElement,
  cloneElement,
} from 'react';
import clsx from 'clsx';
import IconButton from '@mui/material/IconButton';
import MuiDialog from '@mui/material/Dialog';
import CloseIcon from '@mui/icons-material/CloseOutlined';
import MaximizeIcon from 'core/icons/maximize';
import MinimizeIcon from 'core/icons/minimize';

type Props = {
  className?: string;
  open: boolean;
  onClose: () => void;
  children: ReactNode;
  fullScreen?: boolean;
  title?: string;
};

type Maximizable = {
  isFullScreen?: boolean;
};
type MaximizableElement = ReactElement<Maximizable>;

export default function FileViewerDialog({
  open,
  onClose: onDialogClose,
  children: _children,
  title = '',
}: Props) {
  const [fullScreen, setFullScreen] = useState<boolean>(false);

  const FullScreenIcon = useMemo(() => (
    fullScreen ? MinimizeIcon : MaximizeIcon
  ), [fullScreen]);

  const children = Children.map<ReactNode, MaximizableElement>(
    _children as MaximizableElement[],
    (child) => (
      cloneElement(child as ReactElement<Maximizable>, { isFullScreen: fullScreen })
    ),
  );

  const onClose = () => {
    onDialogClose();
    setTimeout(() => {
      setFullScreen(false);
    }, 500);
  };

  const onFullScreen = () => {
    setFullScreen(!fullScreen);
  };

  return (
    <MuiDialog
      className={clsx('populate-dialog file-viewer', {
        'is-preview': !fullScreen,
      })}
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth={fullScreen ? false : 'sm'}
      fullScreen={fullScreen}
    >
      <div className="flex flex-row justify-between items-center bg-primaryLight">
        <div className="flex font-semibold text-xs pl-2 text-white">
          {title}
        </div>
        <div className="flex justify-end gap-2">
          <IconButton onClick={onFullScreen}>
            <FullScreenIcon className="!w-3.5 !h-3.5 !text-white" />
          </IconButton>
          <IconButton onClick={onClose}>
            <CloseIcon className="!w-5 !h-5 !text-white" />
          </IconButton>
        </div>
      </div>
      {children}
    </MuiDialog>
  );
}
